import { Box, Button, TextField, Tooltip, Typography } from '@mui/material'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { useState } from 'react'
import { ClearIcon } from '@mui/x-date-pickers'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import CheckIcon from '@mui/icons-material/Check'
import noop from 'src/utilities/noop'
import { EditIcon } from 'src/v3/core/icons'
import { processImageFile, uploadImageToS3 } from 'src/v3/utilities/s3'
import {
  selectSelectedLinkedService,
  errorMessageChanged,
  selectLinkedServiceById,
  fetchLinkedServices,
} from '../admin.slice'

export default ({ isSaving }: { isSaving: (bool: boolean) => void }) => {
  let dispatch = useAppDispatch()
  let selectedLinkedService = useAppSelector(selectSelectedLinkedService)
  let linkedService = useAppSelector(
    selectLinkedServiceById(selectedLinkedService),
  )
  let [editingName, setEditingName] = useState<boolean>(false)
  let [newName, setNewName] = useState<string>('')

  let onImageUpload = (id: string, t: any) => {
    isSaving(true)
    return axios
      .put('/api/v2/admin/linked-service/image', { id })
      .then((response) => uploadImageToS3(t, response.data))
      .then(() => {
        dispatch(fetchLinkedServices())
        isSaving(false)
      })
  }

  let onLinkedServiceNameChanged = () => {
    isSaving(true)
    if (newName) {
      return axios
        .put('/api/v2/admin/linked-service/name', {
          id: selectedLinkedService,
          name: newName,
        })
        .then(() => {
          dispatch(fetchLinkedServices())
        })
        .catch((err) => {
          dispatch(errorMessageChanged(err?.response?.data))
        })
    }
    return Promise.resolve()
  }

  let handleCancelEditName = () => {
    setNewName('')
    setEditingName(false)
  }

  let handleUpdateName = () => {
    onLinkedServiceNameChanged().then(() => {
      setEditingName(false)
      setNewName('')
      isSaving(false)
    })
  }

  let handleChangeImage = (event: React.ChangeEvent<HTMLInputElement>) => {
    processImageFile(event, {
      onLoadingStart: noop,
      onLoadingEnd: (t: any) => {
        onImageUpload(selectedLinkedService, t)
      },
    })
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        {editingName ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Linked service name"
              fullWidth
              variant="outlined"
              size="small"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoFocus
            />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={handleCancelEditName}
                sx={{ minWidth: 'auto', p: 1 }}
              >
                <ClearIcon sx={{ fontSize: 16 }} />
              </Button>
              <Button
                variant="contained"
                size="small"
                color="success"
                onClick={handleUpdateName}
                sx={{ minWidth: 'auto', p: 1 }}
              >
                <CheckIcon sx={{ fontSize: 16 }} />
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              border: 1,
              borderColor: 'background.paperDark',
              borderRadius: 1,
              backgroundColor: 'background.paper',
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {linkedService?.name}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={() => setEditingName(true)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </Button>
          </Box>
        )}
      </Box>

      <Box>
        <Box
          sx={{
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: 'background.paperDark',
            aspectRatio: '1 / 1',
          }}
        >
          {linkedService?.imageName ? (
            <img
              src={linkedService.imageName}
              alt="Linked service thumbnail"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <AddPhotoAlternateIcon sx={{ fontSize: 48 }} />
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}
          >
            <Tooltip title="Upload Thumbnail">
              <Button
                variant="contained"
                component="label"
                size="small"
                sx={{ minWidth: 'auto', p: 1 }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
                <input
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  hidden
                  onChange={(event) => handleChangeImage(event)}
                />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
